import { LanguageSelectDialogComponent } from '@Components/Dialog/language-select-dialog/language-select-dialog.component';
import { SimpleConfirmDialogComponent } from '@Components/Dialog/simple-confirm-dialog/simple-confirm-dialog.component';
import { SimpleErrorDialogComponent } from '@Components/Dialog/simple-error-dialog/simple-error-dialog.component';
import { ActiveCampaignBannerComponent } from '@Components/Rewards/active-campaign-banner/active-campaign-banner.component';
import { APP_CONFIG } from '@Core/config/app-config';
import { AnagraficaUtente, LangType } from '@Interfaces/interfaces';
import { BaseRewardResponse, CampaignFull, CampaignShort, E_CAMPAIGN_STATUS, RewardsVoucher } from '@Interfaces/rewards.interfaces';
import { AuthService } from '@Services/auth.service';
import { IsToggledService } from '@Services/is-toggled.service';
import { LanguageService } from '@Services/language.service';
import { NavigationService } from '@Services/navigation.service';
import { RewardsService } from '@Services/rewards.service';
import { TicketPurchaseService } from '@Services/ticket-purchase.service';
import { UserService } from '@Services/user.service';
import { clearUserInfo, loadAllUserInfo } from '@State/Actions/user.actions';
import { AppState } from '@State/app.state';
import { HEADER_CONST } from '@Utils/constants';
import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  Renderer2,
  signal,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, Subject, catchError, takeUntil, tap, throwError } from 'rxjs';

type RedirectTo = 'MY_TICKETS' | 'MY_PROFILE' | 'HOME' | "CONTACTS" | "WALLET";

@Component({
    selector: 'b2c-header',
    imports: [
        CommonModule,
        TranslateModule,
        SimpleConfirmDialogComponent,
        SimpleErrorDialogComponent,
        LanguageSelectDialogComponent,
        ActiveCampaignBannerComponent,
    ],
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {

  private readonly config = inject(APP_CONFIG);

  public assetsContextRoot:string = this.config.contextRoot;
  public headerConst = HEADER_CONST;
  public currentLanguage!: LangType;
  @ViewChild('sideMenu') sideMenu!: ElementRef;
  @ViewChild('langDropDown') langDropDown!: ElementRef;
  public anagraficaUtente!:AnagraficaUtente | null;
  public userEmailLong: string = '';
  public userEmail: string = '';
  public isSideBarOpen: boolean = false;
  public isLangDropDownOpen: boolean = false;
  @ViewChild('confirmRedirect') confirmRedirectDialog!: SimpleConfirmDialogComponent;
  @ViewChild('loginErrorDialog') loginErrorDialog!: SimpleErrorDialogComponent;
  @ViewChild('confirmDeleteAccount') confirmDeleteAccountDialog!: SimpleConfirmDialogComponent;
  @ViewChild('accountDeletedSuccess') accountDeletedSuccessDialog!: SimpleConfirmDialogComponent;
  @ViewChild('accountDeletedError') accountDeletedErrorDialog!: SimpleErrorDialogComponent;
  @ViewChild('languageSelectDialog') languageSelectDialog!: LanguageSelectDialogComponent;
  private redirectPath: string = '';
  public isElementNavigable: boolean = true;
  private destroy$ = new Subject<void>();
  public isGroupPaymentPage: boolean = false;

  //* REWARDS
  public readonly campaignStatus = this.rewardsService.campaignStatus;
  public readonly CAMPAIGN_STATUS = E_CAMPAIGN_STATUS;
  public userPointsBalance: Observable<number | null> = this.rewardsService.userPointsBalance$;
  public isLoadingUserPoints = signal(false);
  public userPointsError = signal(false);

  public activeCampaign$: Observable<CampaignShort | null> = this.rewardsService.activeCampaign$;
  public isLoadingCampaingInfo = signal(false);
  public campaignInfoLoadError = signal(false);

  public userActiveVouchers$: Observable<BaseRewardResponse<RewardsVoucher> | null> = this.rewardsService.userActiveVouchers$;
  public isLoadingUserActiveVouchers = signal(false);
  public userActiveVouchersLoadError = signal(false);

  public isUserSubscribedToCurrentCampaign = this.rewardsService.isUserSubscribedToCurrentCampaign;

  public fullActiveCampaingInfo$: Observable<CampaignFull | null> = this.rewardsService.fullCampaignInfo$;

  constructor(
    private languageSvc: LanguageService,
    private ticketSvc:TicketPurchaseService,
    private renderer: Renderer2,
    private authSvc: AuthService,
    private store:Store<AppState>,
    private router:Router,
    private navigationService: NavigationService,
    private translateService: TranslateService,
    private isTogledService: IsToggledService,
    private userSvc: UserService,
    private navigationSvc: NavigationService,
    private rewardsService: RewardsService
  ) {}

  ngOnInit() {
    this.getLanguage();
    this.retrieveUserInfo();
    this.listenForElementNavigation();
    this.listenForGroupPage();

    if(this.isUserLogged){
      this.loadCampaingInfo();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getLanguage(){
    this.languageSvc.getLanguage()
    .pipe(takeUntil(this.destroy$))
    .subscribe((lang) => {
      this.currentLanguage = lang;
    });
  }

  private listenForElementNavigation(){
    this.isTogledService.isElementNavigable()
    .pipe(takeUntil(this.destroy$))
    .subscribe(isNavigable => {
      this.isElementNavigable = isNavigable;
    })
  }

  private listenForGroupPage(){
    this.navigationSvc.isGroupPaymentPage
    .pipe(
      takeUntil(this.destroy$)
    )
    .subscribe((isGroupPaymentPage: boolean) => {
      this.isGroupPaymentPage = isGroupPaymentPage;
    });
  }

  private retrieveUserInfo() {
    if(!this.authSvc.isAuthenticated()) return;
    this.store.dispatch(loadAllUserInfo());

    this.store.select(state => state.user)
    .pipe(takeUntil(this.destroy$))
    .subscribe(userInfo => {
      if(userInfo.loadError){
        this.loginErrorDialog.showModal();
        this.isTogledService.setLoginErrorModalStatus(true);
      }
      if (userInfo.anagraficaUtente && userInfo.infoUtenteLoggato) {
        this.anagraficaUtente = userInfo.anagraficaUtente;
        this.userEmailLong = userInfo.anagraficaUtente.eml;
      }
    });
  }

  public switchLanguage(lang: string) {
    this.languageSvc.switchLanguageFromString(lang);
    this.toggleLangDropDown();
  }

  public toggleSidebar() {
    this.isSideBarOpen = !this.isSideBarOpen;
    this.isTogledService.setSidebarStatus(this.isSideBarOpen);
    let sideMenu = this.sideMenu.nativeElement;

    if (!this.isSideBarOpen) {
      sideMenu.classList.add('sidebar-close');
      sideMenu.classList.remove('sidebar-open');
      this.renderer.removeClass(document.body, 'no-scroll');
    } else {
      sideMenu.classList.remove('sidebar-close');
      sideMenu.classList.add('sidebar-open');
      this.renderer.addClass(document.body, 'no-scroll');
    }

  }

  public toggleLangDropDown() {
    this.isLangDropDownOpen = !this.isLangDropDownOpen;
    this.isTogledService.setLangDropDownStatus(this.isLangDropDownOpen);
    let langDropDOwn = this.langDropDown.nativeElement;
    if (!this.isLangDropDownOpen) {
      langDropDOwn.classList.remove('lang-drop-down-open');
      langDropDOwn.classList.add('lang-drop-down-close');
      this.renderer.removeClass(document.body, 'no-scroll');
    } else {
      langDropDOwn.classList.remove('lang-drop-down-close');
      langDropDOwn.classList.add('lang-drop-down-open');
      this.renderer.addClass(document.body, 'no-scroll');
    }
  }

  // Listener per chiudere dropdown e sidebar da tastiera - supporto screen-reader
  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.isLangDropDownOpen) {
      this.toggleLangDropDown();
    }
    if (event.key === 'Escape' && this.isSideBarOpen) {
      this.toggleSidebar();
    }
  }

  public checkStepAndRedirect(redirectTo: RedirectTo){
    let currentStep = this.navigationService.currentStep.getValue();

    switch (redirectTo) {
      case "MY_TICKETS":
        this.redirectPath = '/myTickets';
        break;

      case "MY_PROFILE":
        this.redirectPath = '/myProfile';
        break;
      case "CONTACTS":
        this.redirectPath = '/contacts';
        break;
      case "WALLET":
        this.redirectPath = '/rewards/wallet';
        break;

      case "HOME":

        if(this.isGroupPaymentPage) {
          window.open("https://www.museiitaliani.it", '_blank', "noopener");
          return;
        }

        this.store.select(state => state.selectedMuseum.museum?.id).pipe(takeUntil(this.destroy$))
        .subscribe(codesr => {
          if(codesr){
            this.redirectPath = `/buyTicketless/${codesr}`;
          } else {
            const LS_SELECTED_MUSEUM = localStorage.getItem('selected-museum');
            if(LS_SELECTED_MUSEUM){
              this.redirectPath = `/buyTicketless/${LS_SELECTED_MUSEUM}`;
            } else {
              this.redirectPath = '/buyTicketless';
            }
          }
        })
        break;

      default:
        this.redirectPath = '/';
        break;
    }

    if(currentStep >= 3){
      this.openRedirectConfirmModal()
    } else {
      if(this.isSideBarOpen) this.toggleSidebar();
      this.ticketSvc.clearAllSelections();
      this.navigationService.resetSteps();
      this.router.navigate([this.redirectPath]);
    }
  }

  public openRedirectConfirmModal(){
    //Prendo la route attuale e vedo se vengo da myTickets o da buyTicketless
    const currentUrl = this.router.url;
    const splitUrl = currentUrl.split('/');
    switch(splitUrl[1]){
      case 'myTickets':
        this.confirmRedirectDialog.title = this.translateService.instant('Header.RESERVATION_CONFIRM_REDIRECT_MODAL_TITLE');
        break;
      case 'buyTicketless':
        this.confirmRedirectDialog.title = this.translateService.instant('Header.PURCHASE_CONFIRM_REDIRECT_MODAL_TITLE');
        break;
    }
    this.confirmRedirectDialog.showModal();
    this.isTogledService.setRedirectConfirmModalStatus(true);
  }

  public redirectForModal(){
    this.ticketSvc.clearAllSelections();
    this.navigationService.resetSteps();
    if(this.isSideBarOpen) this.toggleSidebar();
    this.router.navigate([this.redirectPath]);
    this.confirmRedirectDialog.closeModal();
  }

  public closeDialog(){
    this.isTogledService.setRedirectConfirmModalStatus(false);
    this.confirmRedirectDialog.closeModal();
  }

  get isUserLogged():boolean {
    return this.authSvc.isAuthenticated()
  }

  public logout() {
    this.isTogledService.setLoginErrorModalStatus(false);
    this.store.dispatch(clearUserInfo())
    this.authSvc.logout(false);
  }

  public login(){
    this.authSvc.login();
  }

  public openConfirmDeleteModal(){
    this.confirmDeleteAccountDialog.showModal();
  }

  public deleteUserAccount() {
    this.userSvc
      .deleteUserAccount()
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          this.accountDeletedErrorDialog.showModal();
          return throwError(() => error);
        })
      )
      .subscribe({
        next: (res) => {
          if (res.result.success) {
            this.accountDeletedSuccessDialog.showModal();
          } else {
            this.accountDeletedErrorDialog.showModal();
          }
        },
        error: (err) => {
          this.accountDeletedErrorDialog.showModal();
        },
      });
  }

  public getLangRegionLabel(){
    const locale = new Intl.Locale(`und-${this.currentLanguage.lang_code}`);
    return locale.region;
  }

  public openLanguageSelectModal(){
    this.languageSelectDialog.showModal();
  }

  private loadUserPointsBalance(campaignUuid: string) {
    this.rewardsService.fetchUserPointsBalance(campaignUuid)
    .pipe(
      tap(() => this.isLoadingUserPoints.set(true)),
      takeUntil(this.destroy$)
    ).subscribe({
      next: () => {
        this.isLoadingUserPoints.set(false);
      },
      error: () => {
        this.isLoadingUserPoints.set(false);
        this.userPointsError.set(true);
      }
    })
  }

  private loadCampaingInfo(){
    this.rewardsService.fetchActiveCampaigns()
    .pipe(
      tap(() => this.isLoadingCampaingInfo.set(true)),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (campaignInfo: CampaignShort | null) => {
        this.isLoadingCampaingInfo.set(false);
        if(campaignInfo){
          this.loadFullCampaignInfo(campaignInfo.uuid);
          this.loadUserCampaignSubscription(campaignInfo.uuid);
        }
      },
      error: () => {
        this.isLoadingCampaingInfo.set(false);
        this.campaignInfoLoadError.set(true);
      }
    })
  }

  private loadFullCampaignInfo(uuid: string){
    this.rewardsService.fetchFullCampaign(uuid)
    .pipe(
      takeUntil(this.destroy$)
    ).subscribe({
    })
  }

  private loadUserCampaignSubscription(activeCampaignUuid:string){
    this.rewardsService.fetchUserCampaignSubscriptions(activeCampaignUuid)
    .pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (subsciption) => {
        if(!!subsciption){
          this.loadUserPointsBalance(activeCampaignUuid);
          this.loadUserActiveVouchers(activeCampaignUuid)
        }
      }
    });
  }

  private loadUserActiveVouchers(campaignUuid: string){
    this.rewardsService.fetchUserActiveVouchers(campaignUuid)
    .pipe(
      tap(() => this.isLoadingUserActiveVouchers.set(true)),
      takeUntil(this.destroy$)
    ).subscribe({
      next: () => {
        this.isLoadingUserActiveVouchers.set(false);
      },
      error: () => {
        this.isLoadingUserActiveVouchers.set(false);
        this.userActiveVouchersLoadError.set(true);
      }
    })
  }
}
